if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mediaContainer = this.querySelector('[role="document"]');
        this.pointers = new Map();
        this.zoom = this.defaultZoom();
        this.querySelector('[role="dialog"]')?.addEventListener('pointerup', (event) => event.stopPropagation());
        this.querySelector('.product-media-modal__nav--prev')?.addEventListener('click', () => this.showAdjacentMedia(-1));
        this.querySelector('.product-media-modal__nav--next')?.addEventListener('click', () => this.showAdjacentMedia(1));
        this.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('resize', this.onViewportChange);
      }

      defaultZoom(image = null) {
        return { image, frame: image?.getBoundingClientRect(), scale: 1, x: 0, y: 0, dragging: false, moved: false };
      }

      get isMobile() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      hide() {
        this.disableZoom();
        super.hide();
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.dataset.mediaId;
        this.mediaContainer.querySelectorAll(':scope > [data-media-id]').forEach((item) =>
          item.classList.toggle('active', item.dataset.mediaId === mediaId)
        );
        const activeMedia = this.mediaContainer.querySelector(`:scope > [data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        this.mediaContainer.scrollTo(0, 0);
        this.enableZoom(activeMedia);
        requestAnimationFrame(this.updateNavigationPosition);
        const template = activeMedia.querySelector('template');
        if (activeMedia.nodeName === 'DEFERRED-MEDIA' && template?.content.querySelector('.js-youtube')) {
          activeMedia.loadContent();
        }
      }

      showAdjacentMedia(direction) {
        const items = Array.from(this.mediaContainer.querySelectorAll(':scope > [data-media-id]'));
        if (items.length < 2) return;
        const current = Math.max(0, items.findIndex((item) => item.classList.contains('active')));
        const next = items[(current + direction + items.length) % items.length];
        this.openedBy.dataset.mediaId = next.dataset.mediaId;
        this.showActiveMedia();
      }

      onKeyDown = (event) => {
        if (!this.hasAttribute('open')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.showAdjacentMedia(event.key === 'ArrowRight' ? 1 : -1);
        }
      };

      enableZoom(activeMedia) {
        this.disableZoom();
        const image = activeMedia.matches('img') ? activeMedia : activeMedia.querySelector(':scope > img');
        if (!image) return;

        this.zoom = this.defaultZoom(image);
        image.classList.add('product-media-modal__image--zoomable');
        if (!image.complete) image.addEventListener('load', this.refreshZoomFrame, { once: true });
        if (!this.isMobile) {
          image.addEventListener('click', this.toggleDesktopZoom);
          image.addEventListener('mousemove', this.hoverDesktopZoom);
        }
        image.addEventListener('pointerdown', this.onPointerDown);
        image.addEventListener('pointermove', this.onPointerMove);
        image.addEventListener('pointerup', this.onPointerUp);
        image.addEventListener('pointercancel', this.onPointerUp);
      }

      toggleDesktopZoom = (event) => {
        if (this.zoom.moved) return void (this.zoom.moved = false);
        if (this.zoom.scale > 1) return this.resetTransform();
        this.zoom.scale = 2.2;
        this.focusZoomAt(event.clientX, event.clientY);
        this.renderZoom();
      };

      hoverDesktopZoom = (event) => {
        if (this.isMobile || this.zoom.scale <= 1 || this.zoom.dragging) return;
        this.focusZoomAt(event.clientX, event.clientY);
        this.renderZoom();
      };

      onPointerDown = (event) => {
        const image = event.currentTarget;
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        image.setPointerCapture?.(event.pointerId);

        if (this.pointers.size === 1 && this.zoom.scale <= 1.01) {
          this.startMediaSwipe(event);
        }

        if (this.isMobile && this.pointers.size === 2) {
          this.cleanupMediaSwipe();
          if (this.zoom.scale <= 1.01) this.zoom.frame = image.getBoundingClientRect();
          const center = this.pointerCenter();
          const frame = this.zoom.frame;
          this.pinch = {
            distance: this.pointerDistance(),
            scale: this.zoom.scale,
            contentX: (center.x - frame.left - frame.width / 2 - this.zoom.x) / this.zoom.scale,
            contentY: (center.y - frame.top - frame.height / 2 - this.zoom.y) / this.zoom.scale,
          };
        } else if (this.zoom.scale > 1) {
          this.zoom.lastX = event.clientX;
          this.zoom.lastY = event.clientY;
          this.zoom.dragging = true;
        }
      };

      onPointerMove = (event) => {
        if (!this.pointers.has(event.pointerId)) return;
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.swipe && this.pointers.size === 1 && this.zoom.scale <= 1.01) {
          this.swipe.x = event.clientX;
          this.swipe.y = event.clientY;
          const dx = this.swipe.x - this.swipe.startX;
          const dy = this.swipe.y - this.swipe.startY;
          if (!this.swipe.dragging && Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) {
            this.swipe.dragging = true;
            this.prepareMediaSwipe();
            this.classList.add('is-swiping');
          }
          if (this.swipe.dragging) {
            this.zoom.moved = true;
            this.positionMediaSwipe(dx);
            event.preventDefault();
          }
        }

        if (this.isMobile && this.pointers.size === 2 && this.pinch?.distance) {
          const center = this.pointerCenter();
          const frame = this.zoom.frame;
          this.zoom.scale = Math.min(3.5, Math.max(1, this.pinch.scale * this.pointerDistance() / this.pinch.distance));
          this.zoom.x = center.x - frame.left - frame.width / 2 - this.pinch.contentX * this.zoom.scale;
          this.zoom.y = center.y - frame.top - frame.height / 2 - this.pinch.contentY * this.zoom.scale;
          this.renderZoom();
          event.preventDefault();
          return;
        }

        if (this.zoom.dragging && this.zoom.scale > 1) {
          const dx = event.clientX - this.zoom.lastX;
          const dy = event.clientY - this.zoom.lastY;
          if (Math.abs(dx) + Math.abs(dy) > 2) this.zoom.moved = true;
          this.zoom.x += dx;
          this.zoom.y += dy;
          this.zoom.lastX = event.clientX;
          this.zoom.lastY = event.clientY;
          this.renderZoom();
          event.preventDefault();
        }
      };

      onPointerUp = (event) => {
        const swipe = this.swipe;
        this.pointers.delete(event.pointerId);
        this.pinch = null;
        this.zoom.dragging = false;

        if (swipe && this.pointers.size === 0 && this.zoom.scale <= 1.01) {
          if (!swipe.dragging) {
            this.cleanupMediaSwipe();
            return;
          }
          const dx = swipe.x - swipe.startX;
          const dy = swipe.y - swipe.startY;
          const elapsed = Math.max(performance.now() - swipe.startedAt, 1);
          const velocity = Math.abs(dx) / elapsed;
          const shouldChange = Math.abs(dx) > Math.abs(dy) &&
            (Math.abs(dx) >= Math.min(80, swipe.width * 0.18) || velocity > 0.5);
          this.finishMediaSwipe(shouldChange, dx < 0 ? 1 : -1);
          return;
        }

        if (this.pointers.size === 1 && this.zoom.scale > 1) {
          const pointer = Array.from(this.pointers.values())[0];
          this.zoom.lastX = pointer.x;
          this.zoom.lastY = pointer.y;
          this.zoom.dragging = true;
        }
        if (this.zoom.scale <= 1.01) this.resetTransform();
      };

      pointerCenter() {
        const points = Array.from(this.pointers.values());
        return { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
      }

      pointerDistance() {
        const points = Array.from(this.pointers.values());
        return Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
      }

      startMediaSwipe(event) {
        const items = Array.from(this.mediaContainer.querySelectorAll(':scope > [data-media-id]'));
        if (items.length < 2) return;
        const currentIndex = Math.max(0, items.findIndex((item) => item.classList.contains('active')));
        const current = items[currentIndex];
        const previous = items[(currentIndex - 1 + items.length) % items.length];
        const next = items[(currentIndex + 1) % items.length];
        const width = this.mediaContainer.clientWidth || window.innerWidth;

        this.swipe = {
          startX: event.clientX,
          startY: event.clientY,
          x: event.clientX,
          y: event.clientY,
          startedAt: performance.now(),
          dragging: false,
          width,
          current,
          previous,
          next,
        };
      }

      prepareMediaSwipe() {
        const swipe = this.swipe;
        if (!swipe) return;
        const currentImage = swipe.current.matches('img') ? swipe.current : swipe.current.querySelector(':scope > img');
        const currentVisualWidth = currentImage?.getBoundingClientRect().width || swipe.current.getBoundingClientRect().width;
        if (currentVisualWidth) {
          this.mediaContainer.style.setProperty('--product-modal-swipe-width', `${currentVisualWidth}px`);
        }
        this.mediaContainer.classList.add('is-swipe-ready');
        swipe.current.classList.add('is-swipe-visible', 'is-swipe-current');
        swipe.previous.classList.add('is-swipe-visible', 'is-swipe-previous');
        swipe.next.classList.add('is-swipe-visible', 'is-swipe-next');
        this.positionMediaSwipe(0);
      }

      positionMediaSwipe(dx) {
        const swipe = this.swipe;
        if (!swipe) return;
        const offset = Math.max(-swipe.width, Math.min(swipe.width, dx));
        swipe.current.style.transform = `translate3d(${offset}px, 0, 0)`;
        if (swipe.previous === swipe.next) {
          const adjacentOffset = offset + (offset >= 0 ? -swipe.width : swipe.width);
          swipe.next.style.transform = `translate3d(${adjacentOffset}px, 0, 0)`;
          return;
        }
        swipe.previous.style.transform = `translate3d(${offset - swipe.width}px, 0, 0)`;
        swipe.next.style.transform = `translate3d(${offset + swipe.width}px, 0, 0)`;
      }

      finishMediaSwipe(changeSlide, direction = 0) {
        const swipe = this.swipe;
        if (!swipe || swipe.settling) return;
        swipe.settling = true;
        this.classList.remove('is-swiping');
        this.classList.add('is-swipe-settling');
        const destination = changeSlide ? (direction > 0 ? -swipe.width : swipe.width) : 0;
        requestAnimationFrame(() => this.positionMediaSwipe(destination));

        window.setTimeout(() => {
          this.cleanupMediaSwipe();
          if (changeSlide) this.showAdjacentMedia(direction);
          else this.resetTransform();
        }, 240);
      }

      cleanupMediaSwipe() {
        const swipe = this.swipe;
        if (!swipe) return;
        [swipe.current, swipe.previous, swipe.next].forEach((item) => {
          item.classList.remove('is-swipe-visible', 'is-swipe-current', 'is-swipe-previous', 'is-swipe-next');
          item.style.transform = '';
        });
        this.mediaContainer.classList.remove('is-swipe-ready');
        this.mediaContainer.style.removeProperty('--product-modal-swipe-width');
        this.classList.remove('is-swipe-settling');
        this.swipe = null;
      }

      refreshZoomFrame = () => {
        if (!this.zoom.image) return;
        this.zoom.frame = this.zoom.image.getBoundingClientRect();
        this.renderZoom();
        this.updateNavigationPosition();
      };

      updateNavigationPosition = () => {
        const activeMedia = this.mediaContainer.querySelector(':scope > .active');
        const frame = activeMedia?.matches('.product-media-modal__zoom-frame')
          ? activeMedia.getBoundingClientRect()
          : activeMedia?.getBoundingClientRect();
        if (frame?.height) this.style.setProperty('--product-media-center-y', `${frame.top + frame.height / 2}px`);
      };

      onViewportChange = () => {
        this.resetTransform();
        requestAnimationFrame(this.updateNavigationPosition);
      };

      focusZoomAt(clientX, clientY) {
        const frame = this.zoom.frame;
        const viewport = this.zoom.image.closest('.product-media-modal__zoom-frame')?.getBoundingClientRect() || frame;
        this.zoom.x = viewport.left + viewport.width / 2 - frame.left - frame.width / 2 -
          (clientX - frame.left - frame.width / 2) * this.zoom.scale;
        this.zoom.y = viewport.top + viewport.height / 2 - frame.top - frame.height / 2 -
          (clientY - frame.top - frame.height / 2) * this.zoom.scale;
      }

      renderZoom() {
        const { image, frame, scale } = this.zoom;
        if (!image) return;
        const viewport = image.closest('.product-media-modal__zoom-frame')?.getBoundingClientRect() || frame;
        const halfWidth = frame.width * scale / 2;
        const halfHeight = frame.height * scale / 2;
        const centerX = frame.left + frame.width / 2;
        const centerY = frame.top + frame.height / 2;

        this.zoom.x = scale === 1 || halfWidth * 2 <= viewport.width
          ? 0
          : Math.min(Math.max(this.zoom.x, viewport.right - centerX - halfWidth), viewport.left - centerX + halfWidth);
        this.zoom.y = scale === 1 || halfHeight * 2 <= viewport.height
          ? 0
          : Math.min(Math.max(this.zoom.y, viewport.bottom - centerY - halfHeight), viewport.top - centerY + halfHeight);
        image.classList.toggle('product-media-modal__image--zoomed', scale > 1.01);
        image.style.transform = scale > 1.01 ? `translate3d(${this.zoom.x}px, ${this.zoom.y}px, 0) scale(${scale})` : '';
      }

      resetTransform = () => {
        const image = this.zoom.image;
        if (!image) return;
        image.classList.remove('product-media-modal__image--zoomed');
        image.style.transform = '';
        this.zoom = this.defaultZoom(image);
        this.pointers.clear();
        this.pinch = null;
        this.cleanupMediaSwipe();
      };

      disableZoom() {
        const image = this.zoom.image;
        if (!image) return;
        this.resetTransform();
        image.removeEventListener('click', this.toggleDesktopZoom);
        image.removeEventListener('mousemove', this.hoverDesktopZoom);
        image.removeEventListener('load', this.refreshZoomFrame);
        image.removeEventListener('pointerdown', this.onPointerDown);
        image.removeEventListener('pointermove', this.onPointerMove);
        image.removeEventListener('pointerup', this.onPointerUp);
        image.removeEventListener('pointercancel', this.onPointerUp);
        image.classList.remove('product-media-modal__image--zoomable');
        this.zoom = this.defaultZoom();
      }
    }
  );
}
